<template>
  <div class="pa-4" style="position: relative; min-height: 60vh">
    <v-overlay
      :value="loading"
      absolute
      color="white"
      opacity="0.75"
      z-index="6"
    >
      <v-progress-circular indeterminate color="primary" size="64" width="5" />
    </v-overlay>

    <breadcrumb />

    <div class="d-flex flex-wrap align-center justify-space-between mb-4">
      <h2 class="black--text mb-0">
        {{ $t("reports.aiClient.usageLogTitle") }}
      </h2>
      <v-btn color="primary" depressed @click="goBack">{{
        $t("reports.aiClient.back")
      }}</v-btn>
    </div>

    <v-card outlined class="pa-5">
      <h3 class="black--text mb-4">
        {{ $t("reports.aiClient.usageLogTitle") }}
      </h3>

      <!-- Filters + pagination -->
      <div class="d-flex flex-wrap align-end justify-space-between mb-3">
        <div
          class="d-flex flex-wrap align-end"
          style="gap: 16px; flex: 1 1 auto"
        >
          <v-text-field
            v-model="search"
            :placeholder="$t('reports.aiClient.search')"
            prepend-inner-icon="mdi-magnify"
            outlined
            dense
            hide-details
            clearable
            style="max-width: 320px"
          />
          <div style="max-width: 260px; width: 100%">
            <div class="text-body-2 mb-1">
              {{ $t("reports.aiClient.filterReport") }}
            </div>
            <v-select
              v-model="reportType"
              :items="reportTypeOptions"
              :placeholder="$t('reports.aiClient.makeSelection')"
              outlined
              dense
              hide-details
              clearable
            />
          </div>
        </div>
        <div class="d-flex align-center">
          <span class="grey--text mr-2">{{ rangeLabel }}</span>
          <v-btn
            icon
            outlined
            color="primary"
            class="mr-1"
            :disabled="page <= 1"
            @click="prevPage"
          >
            <v-icon>mdi-chevron-left</v-icon>
          </v-btn>
          <v-btn
            icon
            outlined
            color="primary"
            :disabled="!hasNextPage"
            @click="nextPage"
          >
            <v-icon>mdi-chevron-right</v-icon>
          </v-btn>
        </div>
      </div>

      <v-data-table
        :headers="headers"
        :items="rows"
        :loading="loading"
        :server-items-length="total"
        hide-default-footer
        class="elevation-0 usage-table"
      >
        <template v-slot:[`item.dateTime`]="{ item }">{{
          fmtDateTime(item.dateTime)
        }}</template>
        <template v-slot:[`item.entity`]="{ item }">
          <div class="font-weight-medium black--text">
            {{ item.user || item.cooperative || "System" }}
          </div>
          <div
            class="grey--text text-caption"
            v-if="item.cooperative && item.user"
          >
            {{ item.cooperative }}
          </div>
        </template>
        <template v-slot:[`item.reportType`]="{ item }">{{
          item.reportType ? reportLabel(item.reportType) : "-"
        }}</template>
        <template v-slot:[`item.change`]="{ item }">
          <span class="black--text"
            >{{ formatNum(item.change) }}
            {{ $t("reports.aiClient.credits") }}</span
          >
        </template>
        <template v-slot:[`item.newBalance`]="{ item }">
          <span class="black--text"
            >{{ formatNum(item.newBalance) }}
            {{ $t("reports.aiClient.credits") }}</span
          >
        </template>
      </v-data-table>
    </v-card>
  </div>
</template>

<script>
import AiCreditService from "@/_services/AiCreditService";
import creditFormat from "@/mixins/creditFormat";

const REPORT_TYPES = [
  "vegetation_vigor",
  "farm_productivity",
  "anomaly_detection",
  "yield_prediction",
  "disease_detection",
  "land_suitability",
];

export default {
  name: "ClientCreditsUsageLog",
  mixins: [creditFormat],
  data() {
    return {
      rows: [],
      total: 0,
      page: 1,
      limit: 10,
      search: "",
      reportType: null,
      loading: false,
      searchTimer: null,
    };
  },
  computed: {
    headers() {
      const t = (k) => this.$t(`reports.aiClient.${k}`);
      return [
        { text: t("colDateTime"), value: "dateTime", sortable: false },
        { text: t("colEntity"), value: "entity", sortable: false },
        { text: t("colAiReport"), value: "reportType", sortable: false },
        { text: t("colCreditsUsed"), value: "change", sortable: false },
        { text: t("colBalance"), value: "newBalance", sortable: false },
      ];
    },
    reportTypeOptions() {
      return REPORT_TYPES.map((rt) => ({
        text: this.reportLabel(rt),
        value: rt,
      }));
    },
    hasNextPage() {
      return this.page * this.limit < this.total;
    },
    rangeLabel() {
      const shown = Math.min(this.page * this.limit, this.total);
      return `${shown} ${this.$t("of")} ${this.total}`;
    },
  },
  watch: {
    search() {
      clearTimeout(this.searchTimer);
      this.searchTimer = setTimeout(() => {
        this.page = 1;
        this.fetch();
      }, 500);
    },
    reportType() {
      this.page = 1;
      this.fetch();
    },
  },
  created() {
    this.fetch();
  },
  methods: {
    async fetch() {
      this.loading = true;
      try {
        const params = {
          page: this.page,
          limit: this.limit,
          action: "report_usage",
        };
        if (this.search) params.search = this.search;
        if (this.reportType) params.reportType = this.reportType;
        const env = await AiCreditService.clientUsageLog(params);
        const payload = (env && env.data) || {};
        this.rows = payload.logs || [];
        this.total = (payload.pagination && payload.pagination.total) || 0;
      } catch (e) {
        this.$notify({
          title: this.$t("reports.aiClient.errorTitle"),
          text: this.$t("reports.aiClient.loadFailed"),
          type: "error",
        });
      } finally {
        this.loading = false;
      }
    },
    prevPage() {
      if (this.page > 1) {
        this.page -= 1;
        this.fetch();
      }
    },
    nextPage() {
      if (this.hasNextPage) {
        this.page += 1;
        this.fetch();
      }
    },
    reportLabel(reportType) {
      const key = `reports.aiClient.rt_${reportType}`;
      const label = this.$t(key);
      return label === key ? reportType : label;
    },
    goBack() {
      this.$router.push({ name: "aiReportsDashboard" });
    },
  },
};
</script>

<style scoped>
.usage-table >>> thead th {
  background: #1b7a43;
  color: #fff !important;
  font-weight: 600;
}
</style>
